import {BehaviorSubject, tap} from "rxjs";
import {Page} from "../../models/paginator";
import {HttpClient} from "@angular/common/http";
import {Search} from "../../models/filters";
import {Router} from "@angular/router";
import {DatePipe} from "@angular/common";
import {Meta} from "@angular/platform-browser";

export function getEntityPage<T>(
    pageSubject$: BehaviorSubject<Page<T>>,
    httpClient: HttpClient,
    url: string
): void {
    httpClient
        .get<Page<T>>(url)
        .pipe(tap((entityPage: Page<T>) => pageSubject$.next(entityPage)))
        .subscribe();
}

export function getSearchedEntity<T>(
    search: Search,
    pageSubject$: BehaviorSubject<Page<T>>,
    httpClient: HttpClient,
    url: string
): void {
    httpClient
        .post<Page<T>>(url, search.search)
        .pipe(tap((entityPage: Page<T>) => pageSubject$.next(entityPage)))
        .subscribe();
}

export function goToUrl(router: Router, url: string): void {
    router.navigateByUrl(url).then(() => {
        window.scrollTo(0, 0);
    })
}

export function formatDate(datePipe: DatePipe, date: string): string {
    return datePipe.transform(date, 'd MMM yyyy', undefined, 'mk') ?? '';
}

/**
 * Puts one JSON-LD block in the head, replacing the previous block with the same id.
 * Appending a new block on every navigation left a pile of stale Product entries behind.
 */
export function setStructuredData(id: string, data: object): void {
    document.getElementById(id)?.remove();

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
}

/**
 * Questions and answers written in the admin rich-text content: every heading that ends with
 * "?" is a question, and the text up to the next heading is its answer. Feeds FAQPage schema,
 * so adding an FAQ to a product needs no code - just write it in the content.
 */
export function extractFaq(html: string): { question: string, answer: string }[] {
    if (!html) {
        return [];
    }
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    const faq: { question: string, answer: string }[] = [];
    let current: { question: string, answer: string } | null = null;

    for (const element of Array.from(body.children)) {
        const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
        if (/^H[1-6]$/.test(element.tagName)) {
            current = text.endsWith('?') ? {question: text, answer: ''} : null;
            if (current) {
                faq.push(current);
            }
        } else if (current && text) {
            current.answer = (current.answer + ' ' + text).trim();
        }
    }
    return faq.filter(entry => entry.answer.length > 0);
}

/** Open Graph tags - the preview Facebook, Instagram and Viber show when a link is shared. */
export function setSocialMeta(meta: Meta, tags: {
    title: string,
    description: string,
    url: string,
    image?: string,
    type?: string
}): void {
    meta.updateTag({property: 'og:title', content: tags.title});
    meta.updateTag({property: 'og:description', content: tags.description});
    meta.updateTag({property: 'og:url', content: tags.url});
    meta.updateTag({property: 'og:type', content: tags.type ?? 'website'});
    meta.updateTag({property: 'og:site_name', content: 'Алое вера гел'});
    meta.updateTag({property: 'og:locale', content: 'mk_MK'});
    if (tags.image) {
        meta.updateTag({property: 'og:image', content: tags.image});
    }
}

export function setCanonicalUrl(url: string) {
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
        existingCanonical.remove();
    }

    const link: HTMLLinkElement = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    document.head.appendChild(link);
}

export function truncateString(str: string, maxLength: number): string {
    if (str.length <= maxLength) {
        return str;
    }
    return str.slice(0, maxLength) + '...';
}